import { addToast, Button, Modal, Select } from '@octano/global-ui';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import { TranslationsKeys } from '../../../../locales/translations';
import { useCallback, useEffect, useMemo } from 'react';
import useFirstInstallmentRule from '../hooks/useFirstInstallmentRule';
import { useForm } from 'react-hook-form';
import { Process, ProcessListFormValues } from '../types';
import { useValidations } from '../../../../hooks/useValidations';
import { SectionTitle } from '../../../../components/text';
import { updateTuitionProcess } from '../api';
import { useProcessList } from './ProcessListLoader';
import { formatFirstInstallmentRule, getFirstInstallmentRule } from '../utils';

interface ProcessListEditDialogProps {
  toggle: (process?: Process) => void;
  process: Process;
}

const DEFAULT_VALUES = {
  firstExpirationRule: null,
  hasMultiTuition: null,
  selectDay: null,
  selectMonth: null,
  selectDate: null,
  validityRange: null,
  selectYear: null,
};

export default function ProcessListEditModal({
  toggle,
  process,
}: ProcessListEditDialogProps) {
  const { t } = useTranslation(TranslationsKeys.CONFIGURATION_ACADEMIC_OFFER);

  const { msgValidations } = useValidations();
  const { refresh } = useProcessList();

  const {
    control,
    watch,
    reset,
    handleSubmit,
    setValue,
    formState: { isSubmitting },
  } = useForm<ProcessListFormValues>({
    defaultValues: DEFAULT_VALUES,
  });

  const firstInstallmentRule = watch('firstInstallmentRule');

  const { options, formFields } = useFirstInstallmentRule({
    key: firstInstallmentRule?.value as string,
    control,
  });

  const texts = useMemo(() => {
    const prefix = 'processList.modalForm';
    return {
      title: t(`${prefix}.title`),
      firstExpirationRule: t(`${prefix}.firstExpirationRule`),
      firstExpirationRuleTitle: t(`${prefix}.firstExpirationRuleTitle`),
      firstExpirationRuleSubTitle: t(`${prefix}.firstExpirationRuleSubTitle`),
      hasMultiTuition: t(`${prefix}.hasMultiTuition`),
      success: t(`${prefix}.success`),
      error: t(`${prefix}.error`),
      save: t(`${prefix}.save`),
      close: t(`${prefix}.close`),
    };
  }, [t]);

  const handleClose = useCallback(() => {
    reset(DEFAULT_VALUES);
    toggle(undefined);
  }, [reset, toggle]);

  const onSubmit = useCallback(
    async (data: ProcessListFormValues) => {
      const { hasMultiTuition, ...rest } = data;

      const firstInstallmentRuleValue = formatFirstInstallmentRule(rest);
      if (!firstInstallmentRuleValue) {
        return;
      }

      const response = await updateTuitionProcess({
        tuitionProcessId: process.id,
        data: {
          hasMultiTuition: hasMultiTuition?.value === 'true' ? true : false,
          firstInstallmentRuleValue,
        },
      });

      if (response.status === 200) {
        addToast({
          text: t('processList.modalForm.success'),
          color: 'success',
        });
      } else {
        addToast({
          text: t('processList.modalForm.error'),
          color: 'danger',
        });
      }
      refresh();
      handleClose();
    },
    [t, process.id, refresh, handleClose],
  );

  useEffect(() => {
    if (process.firstInstallmentRule) {
      const {
        key,
        selectDate,
        selectMonth,
        selectYear,
        selectDay,
        validityRange,
      } = getFirstInstallmentRule(process.firstInstallmentRule);

      const selectedOption = options.find((option) => option.value === key);

      return reset({
        hasMultiTuition: {
          label: process.isMultiTuition ? 'Si' : 'No',
          value: process.isMultiTuition ? 'true' : 'false',
        },
        firstInstallmentRule: {
          label: selectedOption?.label ?? '',
          value: selectedOption?.value ?? '',
        },
        selectDate: selectDate ?? null,
        selectMonth: selectMonth ?? null,
        selectYear: selectYear ?? null,
        selectDay: selectDay ?? null,
        validityRange: validityRange?.toString() ?? null,
      });
    }

    return reset({
      hasMultiTuition: {
        label: process.isMultiTuition ? 'Si' : 'No',
        value: process.isMultiTuition ? 'true' : 'false',
      },
    });
  }, [process, setValue, options, reset]);

  return (
    <Modal isOpen toggle={toggle} size="lg" closeOnBackdrop={false}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <Col xs={12}>
            <h3 className="fs-22 text-center mb-3">{texts.title}</h3>
          </Col>
          <Col xs={12} className="mt-3">
            <Select
              name="hasMultiTuition"
              control={control}
              label={texts.hasMultiTuition}
              rules={{ required: msgValidations.required }}
              options={[
                { label: 'Si', value: 'true' },
                { label: 'No', value: 'false' },
              ]}
            />
          </Col>
          <Col xs={12} className="mt-3">
            <SectionTitle text={texts.firstExpirationRuleTitle} />
            <p className="fs-14 mb-3">{texts.firstExpirationRuleSubTitle}</p>
          </Col>
          <Col xs={12} className="mt-2">
            <Select
              label={texts.firstExpirationRule}
              name="firstInstallmentRule"
              options={options}
              control={control}
              placeholder={texts.firstExpirationRule}
              rules={{ required: msgValidations.required }}
            />
          </Col>
          {firstInstallmentRule && <Col xs={12}>{formFields}</Col>}
          <Col xs={12} style={{ marginTop: '10vh' }}>
            <Row>
              <Col xs={6}>
                <Button
                  fullwidth
                  outlined
                  text={texts.close}
                  onClick={handleClose}
                  disabled={isSubmitting}
                />
              </Col>
              <Col xs={6}>
                <Button
                  fullwidth
                  type="submit"
                  text={texts.save}
                  loading={isSubmitting}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </form>
    </Modal>
  );
}
